import type { ColumnConfig, ColumnLayout, TableRowData } from '../types';

/** The table's own default: every column shown, in definition order. */
export const DEFAULT_COLUMN_LAYOUT: ColumnLayout = { order: [], hidden: [] };

/**
 * Resolve the user's saved order against the ids the table renders today.
 *
 * - Saved ids the table no longer renders (a flag turned off, a column the
 *   other Alerts view has) are dropped.
 * - Ids the saved order doesn't know (a column added after the user last
 *   customized) keep their default position: each is inserted right after its
 *   nearest default-order predecessor, or first when it has none.
 */
export function resolveColumnOrder(
  defaultIds: readonly string[],
  savedOrder: readonly string[]
): string[] {
  const known = new Set(defaultIds);
  const result = savedOrder.filter(id => known.has(id));
  const placed = new Set(result);

  defaultIds.forEach((id, index) => {
    if (placed.has(id)) return;
    let insertAt = 0;
    for (let i = index - 1; i >= 0; i--) {
      const predecessorAt = result.indexOf(defaultIds[i]);
      if (predecessorAt !== -1) {
        insertAt = predecessorAt + 1;
        break;
      }
    }
    result.splice(insertAt, 0, id);
    placed.add(id);
  });

  return result;
}

/**
 * Columns the user may customize: every top-level column except the ones the
 * table definition already hides (back-compat filter-only columns, KIR-3769).
 */
export function getCustomizableColumns<TRow extends TableRowData>(
  columns: ColumnConfig<TRow>[]
): ColumnConfig<TRow>[] {
  return columns.filter(column => !column.hidden);
}

/**
 * Apply a saved layout to the column definitions: reorder the customizable
 * columns and mark user-hidden ones `hidden`. Hidden columns stay in the array
 * (rather than being dropped) so their filters, URL state and persisted sort
 * keep validating — the same contract as definition-level `hidden`.
 *
 * `forceVisibleIds` stay shown even when the layout hides them — a column the
 * table is currently filtered or sorted by must keep its header controls, or
 * the filter/sort would apply invisibly (e.g. arriving via a deep link).
 * A layout never hides every column: it can be shared with a table that has
 * other columns, so "all hidden" falls back to showing them all.
 */
export function applyColumnLayout<TRow extends TableRowData>(
  columns: ColumnConfig<TRow>[],
  layout: ColumnLayout,
  forceVisibleIds: ReadonlySet<string> = new Set()
): ColumnConfig<TRow>[] {
  const customizable = getCustomizableColumns(columns);
  const byId = new Map(customizable.map(column => [column.id, column]));
  const order = resolveColumnOrder(
    customizable.map(column => column.id),
    layout.order
  );
  const savedHidden = new Set(layout.hidden);
  // Decided from the saved layout, before force-visible ids are exempted —
  // otherwise filtering one column of an all-hidden layout would end the
  // fallback and hide every other column.
  const hidesEverything = order.every(id => savedHidden.has(id));
  const hidden = new Set(layout.hidden.filter(id => !forceVisibleIds.has(id)));

  const ordered = order.map(id => {
    const column = byId.get(id) as ColumnConfig<TRow>;
    return hidden.has(id) && !hidesEverything
      ? { ...column, hidden: true }
      : column;
  });
  const definitionHidden = columns.filter(column => column.hidden);
  return [...ordered, ...definitionHidden];
}

/** Move the item at `from` to `to`, returning a new array. */
export function moveItem<T>(
  items: readonly T[],
  from: number,
  to: number
): T[] {
  const next = [...items];
  if (from === to || from < 0 || to < 0 || from >= next.length) return next;
  const [moved] = next.splice(from, 1);
  next.splice(Math.min(to, next.length), 0, moved);
  return next;
}

/**
 * Fold a reordered list of the ids this table renders back into the saved
 * order without losing ids it doesn't render. The layout can be shared by
 * tables with different column sets (org vs personal Alerts), so an id only
 * the other table knows keeps its spot after its nearest saved predecessor.
 */
export function mergeColumnOrder(
  savedOrder: readonly string[],
  nextOrder: readonly string[]
): string[] {
  const universe = [
    ...savedOrder,
    ...nextOrder.filter(id => !savedOrder.includes(id)),
  ];
  return resolveColumnOrder(universe, nextOrder);
}
